import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Typography } from '@mui/material';
import { getApiErrorMessage, useApiGet } from '../../utils/httpClientTs';
import { getConversations } from '../../endpoints/messagesTs';
import { Conversation, SortOrder, ConversationListResponse } from '../../types';
import { ServerSideTableColumn } from '../../Components/ServerSideTable';
import ServerSideTable from '../../Components/ServerSideTable';
import CircleIcon from '@mui/icons-material/Circle';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export default function Conversations() {
  const navigate = useNavigate();
  const userId = localStorage.getItem('user_id');

  // Pagination and sorting state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<SortOrder>('desc');
  const [orderBy, setOrderBy] = useState<keyof Conversation>('last_message_at');

  const {
    data: conversationsData,
    isFetching,
    isError,
    error,
    refetch,
  } = useApiGet<ConversationListResponse>(
    ['conversations', page, rowsPerPage, orderBy, order],
    getConversations,
    {
      enabled: userId !== null,
      retry: 1,
      refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    },
    {
      page: page + 1, // API uses 1-based pagination
      per_page: rowsPerPage,
      sort: orderBy,
      order: order,
    }
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing page size
  }, []);

  const handleSort = useCallback(
    (property: keyof Conversation) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [orderBy, order]
  );

  const handleRowClick = useCallback(
    (conversation: Conversation) => {
      navigate(`/conversations/${conversation.id}`);
    },
    [navigate]
  );

  const columns: ServerSideTableColumn<Conversation>[] = [
    {
      id: 'is_read',
      label: '',
      width: '50px',
      firstCell: true,
      sortable: false,
      render: (value: boolean) => (
        <Box display="flex" justifyContent="center" alignItems="center">
          {!value && <CircleIcon color="primary" sx={{ fontSize: 10 }} />}
        </Box>
      ),
    },
    {
      id: 'title',
      label: 'Title',
      width: '30%',
      sortable: true,
      render: (value: string, row: Conversation) => (
        <Typography
          variant="body1"
          fontWeight={row.is_read ? 'normal' : 'bold'}
          color="primary"
          sx={{ cursor: 'pointer' }}
        >
          {value}
        </Typography>
      ),
    },
    {
      id: 'last_message',
      label: 'Last Message',
      width: '45%',
      sortable: false,
      render: (value: string) => (
        <Typography variant="body2" sx={{ fontSize: 14 }}>
          {value}
        </Typography>
      ),
    },
    {
      id: 'last_message_at',
      label: 'Date',
      width: '20%',
      sortable: true,
      render: (value: string) => (
        <Typography variant="caption">
          {dayjs.utc(value).local().format('M/DD/YYYY h:mm A')}
        </Typography>
      ),
    },
  ];

  if (isFetching && !conversationsData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  /* Full-page error only when we have no cached list (e.g. initial load failed). */
  if (!isFetching && isError && !conversationsData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
          Messages
        </Typography>
        <Typography color="error" sx={{ mb: 2 }}>
          {getApiErrorMessage(error)}
        </Typography>
        <Button variant="outlined" onClick={() => refetch()}>
          Retry
        </Button>
      </Box>
    );
  }

  const conversationRows = conversationsData?.data ?? [];
  const pagination = conversationsData?.pagination;
  const totalCount = pagination?.total_count ?? 0;
  /** Null/empty list from Go encodes as `data: null` or []; treat as zero rows. */
  const hasNoConversationsGlobally =
    conversationRows.length === 0 && totalCount === 0;

  if (hasNoConversationsGlobally && !isFetching) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
          Messages
        </Typography>
        {isError && conversationsData && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => void refetch()}
              >
                Retry
              </Button>
            }
          >
            Couldn&apos;t refresh: {getApiErrorMessage(error)}
          </Alert>
        )}
        <Typography>No conversations found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
        Messages
      </Typography>
      {isError && conversationsData && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        >
          Couldn&apos;t refresh: {getApiErrorMessage(error)}
        </Alert>
      )}

      <ServerSideTable
        data={conversationRows}
        columns={columns}
        loading={isFetching}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={pagination?.total_count ?? 0}
        totalPages={pagination?.total_pages ?? 0}
        hasNext={pagination?.has_next ?? false}
        hasPrev={pagination?.has_prev ?? false}
        order={order}
        orderBy={orderBy}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onSort={handleSort}
        onRowClick={handleRowClick}
        emptyMessage="No conversations found."
      />
    </Box>
  );
}
